import type { Transaction } from 'kysely';
import type { LocalDatabase } from '../core/types';
import type {
  Database,
  WorkoutRecord,
  WorkoutExerciseRecord,
  WorkoutSetRecord,
  WorkoutSetValueRecord,
  WorkoutSupersetRecord,
} from '~/powersync/generated/schema';
import type {
  CreateWorkoutInput,
  UpdateWorkoutInput,
  CreateWorkoutExerciseInput,
  UpdateWorkoutExerciseInput,
  UpsertWorkoutSetValueInput,
  UpdateWorkoutSetValueInput,
  CreateWorkoutSupersetInput,
} from './types';
import { RandomUtils } from '@fytness/shared';
import { WorkoutCloner, type WorkoutGraph } from './workout-clone';
import { ExerciseBuilder, type PriorSession } from './exercise-build';

type WorkoutTransaction = Transaction<Database>;

/**
 * Read a workout's full local record graph inside a transaction: the root plus
 * every non-deleted child (dimensions/sets/values reached from non-deleted
 * exercises). Used by both clone paths.
 */
const fetchWorkoutGraph = async (
  trx: WorkoutTransaction,
  workoutId: string,
): Promise<WorkoutGraph> => {
  const workout = await trx
    .selectFrom('Workout')
    .selectAll()
    .where('id', '=', workoutId)
    .where('deletedAt', 'is', null)
    .executeTakeFirst();

  if (!workout) {
    throw new Error(`Workout with id ${workoutId} not found`);
  }

  const supersets = await trx
    .selectFrom('WorkoutSuperset')
    .selectAll()
    .where('workoutId', '=', workoutId)
    .where('deletedAt', 'is', null)
    .orderBy('sortIndex', 'asc')
    .execute();

  const exercises = await trx
    .selectFrom('WorkoutExercise')
    .selectAll()
    .where('workoutId', '=', workoutId)
    .where('deletedAt', 'is', null)
    .orderBy('sortIndex', 'asc')
    .execute();

  const dimensions = await trx
    .selectFrom('WorkoutExerciseDimension')
    .innerJoin(
      'WorkoutExercise',
      'WorkoutExercise.id',
      'WorkoutExerciseDimension.exerciseId',
    )
    .selectAll('WorkoutExerciseDimension')
    .where('WorkoutExerciseDimension.workoutId', '=', workoutId)
    .where('WorkoutExercise.deletedAt', 'is', null)
    .execute();

  const sets = await trx
    .selectFrom('WorkoutSet')
    .selectAll()
    .where('workoutId', '=', workoutId)
    .execute();

  const setValues = await trx
    .selectFrom('WorkoutSetValue')
    .selectAll()
    .where('workoutId', '=', workoutId)
    .execute();

  return { workout, supersets, exercises, dimensions, sets, setValues };
};

/** Persist a freshly cloned graph (skips empty child tables). */
const insertWorkoutGraph = async (
  trx: WorkoutTransaction,
  graph: WorkoutGraph,
): Promise<void> => {
  await trx.insertInto('Workout').values(graph.workout).execute();
  if (graph.supersets.length) {
    await trx.insertInto('WorkoutSuperset').values(graph.supersets).execute();
  }
  if (graph.exercises.length) {
    await trx.insertInto('WorkoutExercise').values(graph.exercises).execute();
  }
  if (graph.dimensions.length) {
    await trx
      .insertInto('WorkoutExerciseDimension')
      .values(graph.dimensions)
      .execute();
  }
  if (graph.sets.length) {
    await trx.insertInto('WorkoutSet').values(graph.sets).execute();
  }
  if (graph.setValues.length) {
    await trx.insertInto('WorkoutSetValue').values(graph.setValues).execute();
  }
};

/**
 * Build a freshly added exercise's full record graph (the exercise, its
 * dimensions, and its sets — cloned from the user's most recent finished
 * session with this activity, or a single empty set) and persist it in one
 * batched write. Runs inside a caller-owned transaction so the whole graph
 * appears atomically instead of trickling in via many separate PowerSync
 * writes. Returns the built exercise record.
 */
const buildAndInsertExerciseGraph = async (
  trx: WorkoutTransaction,
  input: {
    supersetId: string;
    workoutId: string;
    sportActivity: { id: string; defaultDimensions: string };
    sortIndex: number;
  },
  now: string,
): Promise<WorkoutExerciseRecord> => {
  // The most recent finished session that used this activity — the source of
  // the copied sets/values, if any.
  const latestExerciseWithActivity = await trx
    .selectFrom('WorkoutExercise')
    .innerJoin('Workout', 'Workout.id', 'WorkoutExercise.workoutId')
    .select(['WorkoutExercise.id', 'Workout.finishedAt'])
    .where('WorkoutExercise.sportActivityId', '=', input.sportActivity.id)
    .where('WorkoutExercise.deletedAt', 'is', null)
    .where('Workout.deletedAt', 'is', null)
    .where('Workout.finishedAt', 'is not', null)
    .orderBy('Workout.finishedAt', 'desc')
    .executeTakeFirst();

  let priorSession: PriorSession | null = null;
  if (latestExerciseWithActivity) {
    const priorSets = await trx
      .selectFrom('WorkoutSet')
      .selectAll()
      .where('exerciseId', '=', latestExerciseWithActivity.id)
      .execute();

    const priorSetIds = priorSets.map((s) => s.id);
    const priorSetValues = priorSetIds.length
      ? await trx
          .selectFrom('WorkoutSetValue')
          .selectAll()
          .where('setId', 'in', priorSetIds)
          .execute()
      : [];

    const priorDimensions = await trx
      .selectFrom('WorkoutExerciseDimension')
      .selectAll()
      .where('exerciseId', '=', latestExerciseWithActivity.id)
      .execute();

    priorSession = {
      sets: priorSets,
      setValues: priorSetValues,
      dimensions: priorDimensions,
    };
  }

  const graph = ExerciseBuilder.build(
    {
      supersetId: input.supersetId,
      workoutId: input.workoutId,
      sportActivity: input.sportActivity,
      sortIndex: input.sortIndex,
      priorSession,
    },
    { generateId: RandomUtils.uuid, now },
  );

  await trx.insertInto('WorkoutExercise').values(graph.exercise).execute();
  if (graph.dimensions.length) {
    await trx
      .insertInto('WorkoutExerciseDimension')
      .values(graph.dimensions)
      .execute();
  }
  if (graph.sets.length) {
    await trx.insertInto('WorkoutSet').values(graph.sets).execute();
  }
  if (graph.setValues.length) {
    await trx.insertInto('WorkoutSetValue').values(graph.setValues).execute();
  }

  return graph.exercise;
};

export const WorkoutMutations = {
  create: async (
    db: LocalDatabase,
    input: CreateWorkoutInput,
  ): Promise<WorkoutRecord> => {
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const workout: WorkoutRecord = {
      id,
      ...input,
      startedAt: null,
      finishedAt: null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    await db.insertInto('Workout').values(workout).execute();

    return workout;
  },

  update: async (
    db: LocalDatabase,
    id: string,
    input: UpdateWorkoutInput,
  ): Promise<void> => {
    const now = new Date().toISOString();

    await db
      .updateTable('Workout')
      .set({
        ...input,
        updatedAt: now,
      })
      .where('id', '=', id)
      .where('deletedAt', 'is', null)
      .execute();
  },

  softDelete: async (db: LocalDatabase, id: string): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('Workout')
      .set({
        deletedAt: now,
        updatedAt: now,
      })
      .where('id', '=', id)
      .execute();
  },

  // WorkoutExercise mutations - Helper
  //
  // Adds an exercise to an existing superset. The exercise, its dimensions and
  // all of its (copied or empty) sets are built in memory and written in a
  // single transaction, so they appear together in the builder instead of
  // trickling in via many separate PowerSync writes.
  createExerciseForSuperset: async (
    db: LocalDatabase,
    supersetId: string,
    workoutId: string,
    sportActivity: { id: string; defaultDimensions: string },
    sortIndex: number,
  ): Promise<WorkoutExerciseRecord> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();
      return await buildAndInsertExerciseGraph(
        trx,
        { supersetId, workoutId, sportActivity, sortIndex },
        now,
      );
    });
  },

  createExercise: async (
    db: LocalDatabase,
    input: CreateWorkoutExerciseInput,
  ): Promise<WorkoutExerciseRecord> => {
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const exercise: WorkoutExerciseRecord = {
      id,
      note: null,
      ...input,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    await db.insertInto('WorkoutExercise').values(exercise).execute();

    return exercise;
  },

  updateExercise: async (
    db: LocalDatabase,
    id: string,
    input: UpdateWorkoutExerciseInput,
  ): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('WorkoutExercise')
      .set({
        ...input,
        updatedAt: now,
      })
      .where('id', '=', id)
      .where('deletedAt', 'is', null)
      .execute();
  },

  softDeleteExercise: async (db: LocalDatabase, id: string): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('WorkoutExercise')
      .set({
        deletedAt: now,
        updatedAt: now,
      })
      .where('id', '=', id)
      .execute();
  },

  swapExerciseSportActivity: async (
    db: LocalDatabase,
    exerciseId: string,
    newSportActivityId: string,
  ): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('WorkoutExercise')
      .set({
        sportActivityId: newSportActivityId,
        updatedAt: now,
      })
      .where('id', '=', exerciseId)
      .execute();
  },

  moveExerciseUp: async (
    db: LocalDatabase,
    exerciseId: string,
  ): Promise<void> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();

      // Get the current exercise
      const currentExercise = await trx
        .selectFrom('WorkoutExercise')
        .select(['supersetId', 'sortIndex'])
        .where('id', '=', exerciseId)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!currentExercise || currentExercise.sortIndex === 0) {
        return; // Already at the top or doesn't exist
      }

      // Find the exercise above (with sortIndex = currentSortIndex - 1)
      const aboveExercise = await trx
        .selectFrom('WorkoutExercise')
        .select(['id'])
        .where('supersetId', '=', currentExercise.supersetId)
        .where('sortIndex', '=', currentExercise.sortIndex - 1)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!aboveExercise) {
        return; // No exercise above
      }

      // Swap sort indices
      await trx
        .updateTable('WorkoutExercise')
        .set({
          sortIndex: currentExercise.sortIndex - 1,
          updatedAt: now,
        })
        .where('id', '=', exerciseId)
        .execute();

      await trx
        .updateTable('WorkoutExercise')
        .set({
          sortIndex: currentExercise.sortIndex,
          updatedAt: now,
        })
        .where('id', '=', aboveExercise.id)
        .execute();
    });
  },

  moveExerciseDown: async (
    db: LocalDatabase,
    exerciseId: string,
  ): Promise<void> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();

      // Get the current exercise
      const currentExercise = await trx
        .selectFrom('WorkoutExercise')
        .select(['supersetId', 'sortIndex'])
        .where('id', '=', exerciseId)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!currentExercise) {
        return; // Doesn't exist
      }

      // Find the exercise below (with sortIndex = currentSortIndex + 1)
      const belowExercise = await trx
        .selectFrom('WorkoutExercise')
        .select(['id'])
        .where('supersetId', '=', currentExercise.supersetId)
        .where('sortIndex', '=', currentExercise.sortIndex + 1)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!belowExercise) {
        return; // No exercise below
      }

      // Swap sort indices
      await trx
        .updateTable('WorkoutExercise')
        .set({
          sortIndex: currentExercise.sortIndex + 1,
          updatedAt: now,
        })
        .where('id', '=', exerciseId)
        .execute();

      await trx
        .updateTable('WorkoutExercise')
        .set({
          sortIndex: currentExercise.sortIndex,
          updatedAt: now,
        })
        .where('id', '=', belowExercise.id)
        .execute();
    });
  },

  deleteSuperset: async (db: LocalDatabase, id: string): Promise<void> => {
    // First, hard delete all exercises within the superset
    // (This will cascade delete related sets and set values via DB constraints)
    await db
      .deleteFrom('WorkoutExercise')
      .where('supersetId', '=', id)
      .execute();

    // Then, hard delete the superset itself
    await db.deleteFrom('WorkoutSuperset').where('id', '=', id).execute();
  },

  moveSupersetUp: async (
    db: LocalDatabase,
    supersetId: string,
  ): Promise<void> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();

      // Get the current superset
      const currentSuperset = await trx
        .selectFrom('WorkoutSuperset')
        .select(['workoutId', 'sortIndex'])
        .where('id', '=', supersetId)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!currentSuperset || currentSuperset.sortIndex === 0) {
        return; // Already at the top or doesn't exist
      }

      // Find the superset above (with sortIndex = currentSortIndex - 1)
      const aboveSuperset = await trx
        .selectFrom('WorkoutSuperset')
        .select(['id'])
        .where('workoutId', '=', currentSuperset.workoutId)
        .where('sortIndex', '=', currentSuperset.sortIndex - 1)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!aboveSuperset) {
        return; // No superset above
      }

      // Swap sort indices
      await trx
        .updateTable('WorkoutSuperset')
        .set({
          sortIndex: currentSuperset.sortIndex - 1,
          updatedAt: now,
        })
        .where('id', '=', supersetId)
        .execute();

      await trx
        .updateTable('WorkoutSuperset')
        .set({
          sortIndex: currentSuperset.sortIndex,
          updatedAt: now,
        })
        .where('id', '=', aboveSuperset.id)
        .execute();
    });
  },

  moveSupersetDown: async (
    db: LocalDatabase,
    supersetId: string,
  ): Promise<void> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();

      // Get the current superset
      const currentSuperset = await trx
        .selectFrom('WorkoutSuperset')
        .select(['workoutId', 'sortIndex'])
        .where('id', '=', supersetId)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!currentSuperset) {
        return; // Doesn't exist
      }

      // Find the superset below (with sortIndex = currentSortIndex + 1)
      const belowSuperset = await trx
        .selectFrom('WorkoutSuperset')
        .select(['id'])
        .where('workoutId', '=', currentSuperset.workoutId)
        .where('sortIndex', '=', currentSuperset.sortIndex + 1)
        .where('deletedAt', 'is', null)
        .executeTakeFirst();

      if (!belowSuperset) {
        return; // No superset below
      }

      // Swap sort indices
      await trx
        .updateTable('WorkoutSuperset')
        .set({
          sortIndex: currentSuperset.sortIndex + 1,
          updatedAt: now,
        })
        .where('id', '=', supersetId)
        .execute();

      await trx
        .updateTable('WorkoutSuperset')
        .set({
          sortIndex: currentSuperset.sortIndex,
          updatedAt: now,
        })
        .where('id', '=', belowSuperset.id)
        .execute();
    });
  },

  // WorkoutSetValue mutations
  upsertSetValue: async (
    db: LocalDatabase,
    setId: string,
    exerciseDimensionId: string,
    value: number,
    workoutId: string,
  ): Promise<WorkoutSetValueRecord> => {
    const now = new Date().toISOString();

    // Check if record exists
    const existing = await db
      .selectFrom('WorkoutSetValue')
      .selectAll()
      .where('setId', '=', setId)
      .where('exerciseDimensionId', '=', exerciseDimensionId)
      .executeTakeFirst();

    if (existing) {
      // Update existing
      await db
        .updateTable('WorkoutSetValue')
        .set({
          value,
          updatedAt: now,
        })
        .where('id', '=', existing.id)
        .execute();

      return {
        ...existing,
        value,
        updatedAt: now,
      };
    } else {
      // Create new
      const id = RandomUtils.uuid();
      const newRecord: WorkoutSetValueRecord = {
        id,
        setId,
        exerciseDimensionId,
        value,
        workoutId,
        createdAt: now,
        updatedAt: now,
      };

      await db.insertInto('WorkoutSetValue').values(newRecord).execute();

      return newRecord;
    }
  },

  // WorkoutSuperset mutations
  createSuperset: async (
    db: LocalDatabase,
    workoutId: string,
  ): Promise<WorkoutSupersetRecord> => {
    // Get current count to determine sort index
    const countResult = await db
      .selectFrom('WorkoutSuperset')
      .select((eb) => eb.fn.count<number>('id').as('count'))
      .where('workoutId', '=', workoutId)
      .where('deletedAt', 'is', null)
      .execute();

    const sortIndex = countResult[0]?.count ?? 0;
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const superset: WorkoutSupersetRecord = {
      id,
      workoutId,
      sortIndex,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    await db.insertInto('WorkoutSuperset').values(superset).execute();

    return superset;
  },

  createSupersetWithExercise: async (
    db: LocalDatabase,
    workoutId: string,
    sportActivity: { id: string; defaultDimensions: string },
  ): Promise<{ supersetId: string; exerciseId: string }> => {
    // The superset and its first exercise (with dimensions + sets) are written
    // in a single transaction so the whole thing appears at once.
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();

      // Get current superset count to determine sort index
      const countResult = await trx
        .selectFrom('WorkoutSuperset')
        .select((eb) => eb.fn.count<number>('id').as('count'))
        .where('workoutId', '=', workoutId)
        .where('deletedAt', 'is', null)
        .execute();

      const supersetSortIndex = countResult[0]?.count ?? 0;
      const supersetId = RandomUtils.uuid();

      // Create the superset
      await trx
        .insertInto('WorkoutSuperset')
        .values({
          id: supersetId,
          workoutId,
          sortIndex: supersetSortIndex,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        })
        .execute();

      // Create the first exercise within the superset (atomic, same trx).
      const exercise = await buildAndInsertExerciseGraph(
        trx,
        { supersetId, workoutId, sportActivity, sortIndex: 0 },
        now,
      );

      return { supersetId, exerciseId: exercise.id };
    });
  },

  // WorkoutSet mutations - Helper
  createSetForExercise: async (
    db: LocalDatabase,
    exerciseId: string,
    workoutId: string,
    sortIndex: number,
    completedAt?: string | null,
  ): Promise<WorkoutSetRecord> => {
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const setRecord = {
      id,
      exerciseId,
      workoutId,
      sortIndex,
      completedAt: completedAt ?? null,
      createdAt: now,
      updatedAt: now,
    };

    await db.insertInto('WorkoutSet').values(setRecord).execute();

    // Find the most recently created set for this exercise to copy values from
    const mostRecentSet = await db
      .selectFrom('WorkoutSet')
      .selectAll()
      .where('exerciseId', '=', exerciseId)
      .where('id', '!=', id) // Don't include the set we just created
      .orderBy('createdAt', 'desc')
      .executeTakeFirst();

    if (mostRecentSet) {
      // Copy set values from the most recent set
      const existingSetValues = await db
        .selectFrom('WorkoutSetValue')
        .selectAll()
        .where('setId', '=', mostRecentSet.id)
        .execute();

      if (existingSetValues.length > 0) {
        await Promise.all(
          existingSetValues.map((setValue) =>
            WorkoutMutations.upsertSetValue(
              db,
              id,
              setValue.exerciseDimensionId,
              setValue.value,
              workoutId,
            ),
          ),
        );
      }
    }

    return setRecord;
  },

  createSet: async (
    db: LocalDatabase,
    exerciseId: string,
    workoutId: string,
  ): Promise<string> => {
    // Get current count of sets for this exercise to determine sort index
    const countResult = await db
      .selectFrom('WorkoutSet')
      .select((eb) => eb.fn.count<number>('id').as('count'))
      .where('exerciseId', '=', exerciseId)
      .execute();

    const sortIndex = countResult[0]?.count ?? 0;

    const setRecord = await WorkoutMutations.createSetForExercise(
      db,
      exerciseId,
      workoutId,
      sortIndex,
    );

    return setRecord.id;
  },

  deleteSet: async (db: LocalDatabase, setId: string): Promise<void> => {
    return await db.transaction().execute(async (trx) => {
      // Get the set being deleted to know its exercise and sortIndex
      const setToDelete = await trx
        .selectFrom('WorkoutSet')
        .select(['exerciseId', 'sortIndex'])
        .where('id', '=', setId)
        .executeTakeFirst();

      if (!setToDelete) {
        return; // Set doesn't exist
      }

      // Delete the set
      await trx.deleteFrom('WorkoutSet').where('id', '=', setId).execute();

      // Reorder remaining sets in the same exercise
      // Decrease sortIndex by 1 for all sets with higher sortIndex
      await trx
        .updateTable('WorkoutSet')
        .set((eb) => ({
          sortIndex: eb('sortIndex', '-', 1),
          updatedAt: new Date().toISOString(),
        }))
        .where('exerciseId', '=', setToDelete.exerciseId)
        .where('sortIndex', '>', setToDelete.sortIndex)
        .execute();
    });
  },

  completeSet: async (db: LocalDatabase, setId: string): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('WorkoutSet')
      .set({
        completedAt: now,
        updatedAt: now,
      })
      .where('id', '=', setId)
      .execute();
  },

  uncompleteSet: async (db: LocalDatabase, setId: string): Promise<void> => {
    await db
      .updateTable('WorkoutSet')
      .set({
        completedAt: null,
        updatedAt: new Date().toISOString(),
      })
      .where('id', '=', setId)
      .execute();
  },

  /**
   * Clones a workout with all related data (exercises, sets, etc.)
   * This function:
   * 1. Soft deletes the original workout and sets its niceId to null
   * 2. Deep clones the workout including exercises, sets, and more
   * 3. Sets niceId of the new workout to the original workout's niceId
   * 4. References the original workout in clonedFromId
   */
  cloneWorkout: async (
    db: LocalDatabase,
    originalWorkoutId: string,
  ): Promise<string> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();
      const graph = await fetchWorkoutGraph(trx, originalWorkoutId);

      // Replace-in-place: retire the original so the clone can inherit its
      // niceId without colliding on the unique index.
      await trx
        .updateTable('Workout')
        .set({ deletedAt: now, niceId: null, updatedAt: now })
        .where('id', '=', originalWorkoutId)
        .execute();

      const cloned = WorkoutCloner.clone(graph, {
        generateId: RandomUtils.uuid,
        now,
        niceId: graph.workout.niceId,
        clonedFromId: originalWorkoutId,
        plannedAt: graph.workout.plannedAt,
        // Born STARTED in the same transaction: the start flow navigates to
        // /progress by the inherited niceId, and WorkoutProgress redirects any
        // PLANNED workout to the builder. A separate startWorkout left a transient
        // PLANNED clone that lost the /progress race, so finishing never showed
        // the summary.
        startedAt: now,
      });

      await insertWorkoutGraph(trx, cloned);

      return cloned.workout.id;
    });
  },

  /**
   * Duplicates a workout into a **new planned session** on a chosen date,
   * keeping both workouts (unlike {@link WorkoutMutations.cloneWorkout}, which
   * replaces the original). The copy reproduces the full structure and planned
   * values with completion state reset; it is a fresh planned workout with a new
   * uuid, `niceId = null` (PowerSync backfills it — the list handles the
   * pre-sync state) and `clonedFromId = null` (the column is `@unique`, so
   * leaving it null allows a source to be duplicated more than once). The
   * original is left untouched.
   */
  duplicateWorkout: async (
    db: LocalDatabase,
    originalWorkoutId: string,
    { plannedAt }: { plannedAt: string },
  ): Promise<string> => {
    return await db.transaction().execute(async (trx) => {
      const now = new Date().toISOString();
      const graph = await fetchWorkoutGraph(trx, originalWorkoutId);

      const cloned = WorkoutCloner.clone(graph, {
        generateId: RandomUtils.uuid,
        now,
        niceId: null,
        clonedFromId: null,
        plannedAt,
      });

      await insertWorkoutGraph(trx, cloned);

      return cloned.workout.id;
    });
  },

  /**
   * Starts a workout by setting the startedAt timestamp
   */
  startWorkout: async (db: LocalDatabase, workoutId: string): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('Workout')
      .set({
        startedAt: now,
        finishedAt: null, // Clear finishedAt in case workout was previously finished
        updatedAt: now,
      })
      .where('id', '=', workoutId)
      .where('deletedAt', 'is', null)
      .execute();
  },

  /**
   * Clones a workout and starts the new workout.
   * Combines cloneWorkout and startWorkout in a single operation.
   */
  cloneAndStartWorkout: async (
    db: LocalDatabase,
    originalWorkoutId: string,
  ): Promise<string> => {
    // cloneWorkout already stamps startedAt inside its transaction (born STARTED),
    // so no separate startWorkout — that second write left a transient PLANNED
    // clone that raced the /progress redirect.
    const newWorkoutId = await WorkoutMutations.cloneWorkout(
      db,
      originalWorkoutId,
    );
    return newWorkoutId;
  },
};
